import { useSyncExternalStore } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const KEY = 'sirius.theme.v1';

/**
 * Тема інтерфейсу — спільна для клієнта й адмінки.
 *
 * Живе в `packages/ui`, бо це один і той самий механізм у двох застосунках:
 * ключ у localStorage, атрибут на <html> і стеження за системною темою мусять
 * збігатися, інакше сторінки починають блимати різними темами.
 *
 * Зберігаємо ВПОДОБАННЯ (включно з «system»), а не обчислений колір: інакше
 * користувач, який обрав «як у системі», перестане слідувати за нею, щойно
 * система перемкнеться.
 *
 * Кожен доступ до localStorage у try/catch: у приватному режимі й за
 * заблокованих site data навіть читання кидає виняток.
 */
function readPreference(): ThemePreference {
	try {
		const raw = localStorage.getItem(KEY);
		return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'system';
	} catch {
		return 'system';
	}
}

function systemTheme(): ResolvedTheme {
	return typeof window !== 'undefined' &&
		window.matchMedia('(prefers-color-scheme: dark)').matches
		? 'dark'
		: 'light';
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
	return preference === 'system' ? systemTheme() : preference;
}

/** Атрибут на <html> — саме за ним CSS підміняє значення токенів. */
function apply(preference: ThemePreference): void {
	document.documentElement.dataset.theme = resolveTheme(preference);
}

let preference: ThemePreference = readPreference();
const listeners = new Set<() => void>();

function emit(): void {
	for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
	listeners.add(listener);

	// Якщо обрано «як у системі», стежимо за зміною системної теми.
	const media = window.matchMedia('(prefers-color-scheme: dark)');
	const onSystemChange = (): void => {
		if (preference === 'system') {
			apply(preference);
			emit();
		}
	};
	media.addEventListener('change', onSystemChange);

	// Синхронізація між вкладками.
	const onStorage = (e: StorageEvent): void => {
		if (e.key !== KEY) return;
		preference = readPreference();
		apply(preference);
		emit();
	};
	window.addEventListener('storage', onStorage);

	return (): void => {
		listeners.delete(listener);
		media.removeEventListener('change', onSystemChange);
		window.removeEventListener('storage', onStorage);
	};
}

export interface ThemeApi {
	preference: ThemePreference;
	resolved: ResolvedTheme;
	setPreference: (next: ThemePreference) => void;
	/** Швидке перемикання світла ↔ темрява, минаючи «system». */
	toggle: () => void;
}

export function useTheme(): ThemeApi {
	const value = useSyncExternalStore<ThemePreference>(
		subscribe,
		() => preference,
		// На сервері системної теми не існує — віддаємо нейтральне значення.
		() => 'system',
	);

	const setPreference = (next: ThemePreference): void => {
		preference = next;
		try {
			localStorage.setItem(KEY, next);
		} catch {
			// Немає сховища — тема просто не переживе перезавантаження.
		}
		apply(next);
		emit();
	};

	return {
		preference: value,
		resolved: resolveTheme(value),
		setPreference,
		toggle: (): void => setPreference(resolveTheme(value) === 'dark' ? 'light' : 'dark'),
	};
}
